import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {useMapSelector} from '../../../../hooks/map-selector';
import {SelectItem} from '../../../../components/Select';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {isValidSIPNo} from '../../../../utils/validation';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from '../../../../hooks/localization';
import {ConciergeModel} from '../../../../types/concierge';
import {ConciergeTypeModel} from '../../../../types/concierge-type';

const BACK_URL = '/settings/concierge';

export const ConciergeEditScreen = memo(function ConciergeEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const conciergeList = useSelector(state => state.control.conciergeList);
    const conciergeTypeOptionList = useMapSelector(
        state => state.control.conciergeTypeList,
        type => ({value: type, label: type.name}),
    ) || [];

    const [selected, setSelected] = useState<ConciergeModel>(null);
    const [name, setName] = useState('');
    const [conciergeTypeOption, setConciergeTypeOption] = useState<SelectItem<ConciergeTypeModel>>(null);
    const [conciergeURL, setConciergeURL] = useState('');
    const [sipNumber, setSipNumber] = useState('');
    const [keyNumber, setKeyNumber] = useState('')
    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t('pls_enter_a_valid_concierge_name'));

            return false;
        }

        if (conciergeTypeOption == null) {
            failure(t('pls_select_a_valid_concierge_type'));

            return false;
        }

        if (conciergeTypeOption.value.types.includes('url')) {
            if (conciergeURL == null || conciergeURL.length === 0) {
                failure(t('pls_enter_a_valid_url'));

                return false;
            }
        }

        if (conciergeTypeOption.value.types.includes("key")) {
            if (keyNumber == null || keyNumber.length === 0) {
                failure(t("concierge_page.pls_enter_a_valid_api_key"))

                return false
            }
        }

        if (conciergeTypeOption.value.types.includes('sip')) {
            if (!isValidSIPNo(sipNumber)) {
                failure(t('pls_enter_a_valid_sip_no'));

                return false;
            }
        }

        return true;
    }, [t, conciergeTypeOption, conciergeURL, sipNumber, failure, name, keyNumber]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const conciergeModel: ConciergeModel = {
            id: selected?.id || null,
            name,
            type_id: conciergeTypeOption.value.id,
            info: {},
        };

        if (conciergeTypeOption.value.types.includes('url')) {
            let conciergeURLWithScheme = conciergeURL;

            if (conciergeURLWithScheme != null && conciergeURLWithScheme.indexOf('http://') !== 0) {
                conciergeURLWithScheme = 'http://' + conciergeURL;
            }

            conciergeModel.info.url = conciergeURLWithScheme;
        }

        if (conciergeTypeOption.value.types.includes('sip')) {
            conciergeModel.info.sip = sipNumber;
        }

        if (conciergeTypeOption.value.types.includes("key")) {
            conciergeModel.info.key = keyNumber;
        }

        return rpc.updateConcierge(conciergeModel)
            .then(() => {
                success(t(conciergeModel.id ? 'concierge_successfully_edited' : 'concierge_successfully_created'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        conciergeTypeOption?.value,
        conciergeURL,
        sipNumber,
        failure,
        isFieldsValid,
        name,
        onBackPressed,
        rpc,
        selected?.id,
        success,
        keyNumber
    ]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteConcierge(selected.id)
            .then(() => {
                success(t('concierge_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
    }, [t, failure, onBackPressed, rpc, selected, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    const buttons = useMemo(() => [
        {
            component: selected == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type="concierge"
                    questionText={t('are_you_sure_you_want_to_delete')}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue', {
                        name: selected?.name,
                        message,
                    })}
                    onPress={onDeletePressed}/>,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        }], [t, selected, onSavePressed, onDeletePressed],
    );

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const concierge = conciergeList.find(conciergeItem => conciergeItem.id === params['id']);
        if (concierge == null) {
            return;
        }

        setSelected(concierge);
        setName(concierge.name);
        setConciergeTypeOption(
            conciergeTypeOptionList.find(conciergeTypeOption => conciergeTypeOption.value.id === concierge.type_id) ||
            conciergeTypeOptionList[0] ||
            null);
        setConciergeURL(concierge.info.url);
        setKeyNumber(concierge.info.key)
        setSipNumber(concierge.info.sip);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setConciergeTypeOption(conciergeTypeOptionList[0] || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t('write_concierge_name')}
                            label={t('concierge_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('concierge_type')}
                            options={conciergeTypeOptionList}
                            onChange={setConciergeTypeOption}
                            value={conciergeTypeOption}/>
                        {
                            renderIf(conciergeTypeOption?.value?.types?.includes('sip'))(() => (
                                <SettingInputComponent
                                    placeholder={t('write_sip_no')}
                                    label={t('sip_no')}
                                    value={sipNumber}
                                    setValue={setSipNumber}
                                    setActiveInput={setActiveNumpadInput}
                                    onBlur={onBlur}/>
                            ))
                        }
                        {
                            renderIf(conciergeTypeOption?.value?.types?.includes('key'))(() => (
                                <SettingInputComponent
                                    placeholder={t("concierge_page.enter_api_key")}
                                    label={t("concierge_page.api_key")}
                                    value={keyNumber}
                                    setValue={setKeyNumber}
                                    setActiveInput={setActiveNumpadInput}
                                    onBlur={onBlur}/>
                            ))
                        }
                        {
                            renderIf(conciergeTypeOption?.value?.types?.includes('url'))(() => (
                                <SettingInputComponent
                                    maxLength={200}
                                    placeholder={t('write_url')}
                                    label={t('url_ip')}
                                    value={conciergeURL}
                                    setValue={setConciergeURL}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}/>
                            ))
                        }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
